import React, { useState, useEffect } from "react";
import { hexToRgbArray, rgbArrayToHex } from "../../utils/colors.js";
import "./ColorsSchemes.css";
import { v4 as uuidv4 } from "uuid";
import handleClick from "../../utils/handleClick.js";
import { INITIAL_COLOR_SCHEMES } from "../../utils/constants.js";
import { getCachedScript } from "../../utils/scriptCache.js";

const COLOR_SCHEMES = "colorSchemes";

function ColorsSchemes({
  isVisible,
  setIsVisible,
  useCepStorage,
  saveCepStorage,
  getCepStorage,
  optionsPath,
  selectedTag,
  buttonTags,
  generateButtonTags,
}) {
  const [colors, setColors] = useState([]);
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem("currentPage");
    return savedPage ? parseInt(savedPage, 10) : 0;
  });
  const [ignoreBWColor, setIgnoreBWColor] = useCepStorage(
    `${optionsPath}.ignoreBWColor`
  );

  useEffect(() => {
    const savedColors = getCepStorage(COLOR_SCHEMES);
    if (!savedColors) {
      const colorSchemesWithId = INITIAL_COLOR_SCHEMES.slice(0, 100).map(
        (scheme) => ({ id: uuidv4(), colors: scheme })
      ); // Ограничим для примера
      saveCepStorage(COLOR_SCHEMES, colorSchemesWithId);
      setColors(colorSchemesWithId);
    } else {
      setColors(savedColors);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("currentPage", currentPage.toString());
  }, [currentPage]);

  const schemesPerPage = 5;
  const currentSchemes = colors.slice(
    currentPage * schemesPerPage,
    (currentPage + 1) * schemesPerPage
  );

  const changeColor = (color, schemeId, colorIndex) => {
    const startColor = hexToRgbArray(color);
    const code = getCachedScript();
      if (code) {
    window.jsx.evalScript(
      "run($.ae.colorPicker(" + JSON.stringify(startColor) + "));",
      function (data) {
        if (data) {
          if (startColor.join(",") !== data) {
            let rgbArray = data.split(",").map((value) => parseFloat(value));
            let newColor = rgbArrayToHex(rgbArray);

            const newColors = colors.map((scheme) => {
              if (scheme.id === schemeId) {
                const newSchemeColors = [...scheme.colors];
                newSchemeColors[colorIndex] = newColor; // Используем HEX-код напрямую
                return { ...scheme, colors: newSchemeColors };
              }
              return scheme;
            });

            setColors(newColors);
            saveCepStorage(COLOR_SCHEMES, newColors);
          }
        }
      }
    );
  };
  };

  const handleDeleteScheme = (id) => {
    const filteredColors = colors.filter((scheme) => scheme.id !== id);
    setColors(filteredColors);
    saveCepStorage(COLOR_SCHEMES, filteredColors);

    // Обновляем количество страниц после удаления
    const newPageCount = Math.ceil(filteredColors.length / schemesPerPage);

    // Если после удаления текущая страница больше не существует (например, была удалена последняя схема на последней странице)
    if (currentPage >= newPageCount) {
      // Устанавливаем currentPage на последнюю существующую страницу, если это возможно, иначе на первую страницу
      const newCurrentPage = newPageCount > 0 ? newPageCount - 1 : 0;
      setCurrentPage(newCurrentPage);
      localStorage.setItem("currentPage", newCurrentPage.toString());
    }

    // Проверяем, остались ли схемы на текущей странице
    const startIndex = currentPage * schemesPerPage;
    if (!filteredColors.slice(startIndex, startIndex + schemesPerPage).length) {
      // Здесь можно добавить логику для перехода на предыдущую страницу или обновления интерфейса, чтобы отобразить сообщение о пустой странице.
      // Пример перенаправления на предыдущую страницу:
      handlePrev(); // Убедитесь, что handlePrev корректно обрабатывает случай с переходом на последнюю страницу, когда currentPage === 0
    }
  };

  const handleAddScheme = () => {
    const code = getCachedScript();
      if (code) {
    window.jsx.evalScript(`run(getColorsScheme(${false}));`, function (data) {
      if (data) {
        const rgbaScheme = JSON.parse(data); // Предполагается, что это массив цветов в формате RGB

        // Преобразование конкретной схемы цветов в HEX, исключая альфа-канал
        const newScheme = rgbaScheme.map((color) =>
          rgbArrayToHex(color.slice(0, 3))
        );

        // Создаём объект схемы с уникальным идентификатором
        const newSchemeWithId = { id: uuidv4(), colors: newScheme };

        // Обновляем массив цветовых схем
        const updatedColors = [...colors, newSchemeWithId];
        setColors(updatedColors);

        // Сохраняем обновлённый массив схем в localStorage
        localStorage.setItem(COLOR_SCHEMES, JSON.stringify(updatedColors));

        // Вычисляем номер страницы для новой схемы
        const newPageIndex =
          Math.ceil(updatedColors.length / schemesPerPage) - 1;
        setCurrentPage(newPageIndex);
        localStorage.setItem("currentPage", newPageIndex.toString());
      }
    });
  }
  };

  const handlePrev = () => {
    const newPageIndex =
      currentPage === 0
        ? Math.ceil(colors.length / schemesPerPage) - 1
        : currentPage - 1;
    setCurrentPage(newPageIndex);
    localStorage.setItem("currentPage", newPageIndex.toString());
  };

  const handleNext = () => {
    const newPageIndex =
      currentPage === Math.ceil(colors.length / schemesPerPage) - 1
        ? 0
        : currentPage + 1;
    setCurrentPage(newPageIndex);
    localStorage.setItem("currentPage", newPageIndex.toString());
  };

  const handleRandom = () =>
    setCurrentPage(
      Math.floor(Math.random() * Math.ceil(colors.length / schemesPerPage))
    );

  if (selectedTag && !buttonTags.includes(selectedTag)) {
    return null;
  }

  return (
    <>
      <li
        onClick={() => setIsVisible(!isVisible)}
        className={`button-card resolution-toggle ${isVisible ? "active" : ""}`}
      >
        <div className="button-card__icon">
          <i class="fa-solid fa-eye-dropper"></i>
        </div>
        <div className="button-card__title">Color Schemes</div>
        {generateButtonTags()}
      </li>
      {isVisible && (
        <div className="colors-schemes">
          <div className="render-group__checkbox">
            <input
              type="checkbox"
              id="ignoreBWColor"
              checked={ignoreBWColor}
              onChange={() => {
                setIgnoreBWColor(!ignoreBWColor);
              }}
              className="hidden-checkbox"
            />
            <label htmlFor="ignoreBWColor">
              <span className="custom-checkbox"></span>
              <span className="label-text">Skip B&W Color</span>
            </label>
          </div>
          {currentSchemes.map((scheme) => (
            <div key={scheme.id} className="color-scheme-container">
              <div className="color-scheme">
                {scheme.colors.map((color, index) => (
                  <div
                    key={index}
                    className="color-block"
                    style={{ backgroundColor: color }}
                    onClick={() => changeColor(color, scheme.id, index)}
                  ></div>
                ))}
              </div>
              <div className="color-scheme-action">
                <span
                  className="apply-span"
                  title="Add Colors"
                  onClick={() =>
                    handleClick(
                      "createColorsScheme",
                      scheme.colors.map(hexToRgbArray)
                    )
                  }
                >
                  <i class="fa-regular fa-arrow-down"></i>
                </span>
                <span
                  className="apply-span"
                  title="Index"
                  onClick={() =>
                    handleClick("updateColorsScheme", {
                      colors: scheme.colors.map(hexToRgbArray),
                      bwColor: ignoreBWColor,
                    })
                  }
                >
                  <i class="fa-regular fa-arrow-right"></i>
                </span>
                <span
                  className="apply-span"
                  title="Random"
                  onClick={() =>
                    handleClick("updateColorsScheme", {
                      colors: scheme.colors
                        .map(hexToRgbArray)
                        .sort(function () {
                          return Math.random() - 0.5;
                        }),
                      bwColor: ignoreBWColor,
                    })
                  }
                >
                  <i class="fa-regular fa-shuffle"></i>
                </span>
                <span
                  className="apply-span"
                  title="Reverse"
                  onClick={() =>
                    handleClick("updateColorsScheme", {
                      colors: scheme.colors
                        .map(hexToRgbArray)
                        .slice()
                        .reverse(),
                      bwColor: ignoreBWColor,
                    })
                  }
                >
                  <i class="fa-solid fa-arrow-left"></i>
                </span>
                <span
                  className="apply-span"
                  title="Delete"
                  onClick={() => handleDeleteScheme(scheme.id)}
                >
                  <i class="fa-regular fa-trash"></i>
                </span>
              </div>
            </div>
          ))}
          <div
            title="New Schemes"
            className="scheme-navigation-btn scheme-navigation-add"
            onClick={handleAddScheme}
          >
            <i class="fa-solid fa-plus"></i>
          </div>
          <div className="scheme-navigation">
            <div className="scheme-navigation-btn" onClick={handlePrev}>
              &lt;
            </div>
            <div className="scheme-navigation-btn" onClick={handleRandom}>
              <i class="fa-solid fa-rotate"></i>
            </div>
            <div className="scheme-navigation-btn" onClick={handleNext}>
              &gt;
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ColorsSchemes;
